import HomeSequential from '@/pages/home/components/home-sequential';
import RechargeGiftModalController from '@/pages/home/components/recharge-gift/RechargeGiftModalController';
import {DialogLoading} from '@basicComponents/dialog';
import {useConfirm, useToast} from '@basicComponents/modal';
import {ModalManagerProvider} from '@basicComponents/sequential-modal/ModalManagerProvider';
import {NavigationContainer} from '@react-navigation/native';
import {
  CardStyleInterpolators,
  createStackNavigator,
} from '@react-navigation/stack';
import {
  getBalance,
  getMessageNotReadCount,
  postReadMessage,
} from '@services/global.service';
import theme from '@style';
import {BasicObject, SafeAny} from '@types';
import {
  envConfig,
  getUrlParams,
  getUUID,
  goTo,
  inApp,
  navigationRef,
} from '@utils';
import {useDebounceFn} from 'ahooks';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Platform, StatusBar, View} from 'react-native';
import {SafeAreaProvider, SafeAreaView} from 'react-native-safe-area-context';
import {debounceTime, Subject, takeUntil, throttleTime} from 'rxjs';
import {dailyRecord, setToken, setUserInfo, setVisitor} from './app.service';
import {useVersionModal} from './common-pages/hooks/versionmodal.hooks';
import {resolveLanguage} from './i18n';
import {VipUpgradeProvider} from './common-pages/vip/provider/vip-upgrade-provider';
import {ParabolaProvider} from './components/basic/parabola';
import Spin from './components/basic/spin';
import DetailNavTitle from './components/business/detail-nav-title';
import PushBox from './components/business/push-box/push-box';
import {defaultChannel} from './config';
import MainNav from './main-navigation';
import {linking, routes} from './route';
import globalStore from './services/global.state';
import {setAdidAndTrackerName, setupAdjust} from './utils/adjust';
import {checkUpdate as checkUpdateNew} from './utils/update';
// import {ImBox} from '@/common-pages/im/im-box';

const myPromiseAllSettled: PromiseConstructor['allSettled'] = <
  T extends readonly unknown[] | [],
>(
  promisesArr: T,
) => {
  let wrappedPromises = promisesArr.map(promise =>
    // instead of doing value.then(), we have to use
    // Promise.resolve(value) so that we can convert
    // a non "promise" input into a "promise", and later
    // chain it with .then()
    Promise.resolve(promise)

      // when the promise is resolved, we return an object
      // with status and the value
      .then(val => ({status: 'fulfilled' as 'fulfilled', value: val}))

      // when the promise is rejected, we return an object
      // with the status and the reason
      .catch(err => ({status: 'rejected' as 'rejected', reason: err})),
  );

  // now the wrappedPromises array will contain all the settled promises
  // we will send this to Promise.all() which will resolve every time!
  return Promise.all(wrappedPromises);
  // you can also use the polyfill from part 1 of the series and it'll work the same
};

if (!Promise.allSettled) {
  Promise.allSettled = myPromiseAllSettled;
}
import SharePanelProvider from '@/components/business/share-panel/share-panel-provider';
import {useH5MigrationGuard} from '@/common-pages/hooks/h5-migration.hooks';
import SportsIframeHost from '@/pages/gr8-sports/gr8-host';

setVisitor(getUUID());

declare var CodePush: any;
declare var AppWithCodePush: any;
if (globalStore.isAndroid) {
  CodePush = require('react-native-code-push');
}
const Stack = createStackNavigator();
const params = getUrlParams();

const isHomeSequentialPath = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.location.pathname.includes('/index/home');
};

const isHomeSequentialRoute = (routeName?: string) => {
  if (routeName) {
    return routeName === 'Home';
  }

  return isHomeSequentialPath();
};

if (params.c) {
  globalStore.promotionChannelId = params.c;
}
if (params.l) {
  globalStore.promotionLinkCode = params.l;
}

function App(): JSX.Element {
  if (params.channel) {
    globalStore.channel = params.channel;
  }
  if (params.code) {
    localStorage.setItem('invitationCode', params.code);
  }
  const appDistory = new Subject<boolean>();
  const {i18n} = useTranslation();
  const remoteBundleRef = React.useRef<null | SafeAny>();
  const downloadLock = React.useRef<boolean>(false);
  const [homeSequentialReady, setHomeSequentialReady] = React.useState(() =>
    isHomeSequentialRoute(),
  );
  const [globalLoading, setGlobalLoading] = React.useState(false);
  const h5MigrationMode = useH5MigrationGuard();
  const shouldShowH5MigrationGuide = h5MigrationMode === 'guide';
  const {renderModal: renderToast, show: toastShow} = useToast();
  const {renderModal: renderDownloadToast, show: downloadToastShow} = useToast({
    backDropClose: false,
    delayClose: 5000,
  });

  const initChat = () => {
    const chatModule = require('@components/chat');
    const freshchatConfig = new chatModule.FreshchatConfig(
      '2723cbe3-2d77-49a8-9aee-93f9f8a97517',
      'a56bcd20-e428-4b14-b2b3-ed7745e6d6f2',
    );
    freshchatConfig.domain = 'msdk.freshchat.com';
    chatModule.Freshchat.init(freshchatConfig);
  };

  const {renderModal: renderConfirmModal, show: confirmShow} = useConfirm(
    'Update Now',
    'Later',
  );

  /** 全局订阅 */
  const globalSubscriptions = () => {
    globalStore.globalLoading.pipe(takeUntil(appDistory)).subscribe(bool => {
      setGlobalLoading(bool);
    });
    if (!globalStore.isWeb) {
      globalStore.globalTotal.pipe(takeUntil(appDistory)).subscribe(config => {
        config.message = config.message;
        toastShow(config);
      });
    }
    globalStore.tokenSubject.pipe(takeUntil(appDistory)).subscribe(token => {
      if (!token) {
        globalStore.notificationSubject.next({
          messageTotalCount: 0,
          sysMessageCount: 0,
          sysUserMessageCount: 0,
        });
        return;
      }
      setTimeout(() => {
        globalStore.updateAmount.next({});
      }, 100);
    });
    globalStore.refreshNotification
      .pipe(debounceTime(500), takeUntil(appDistory))
      .subscribe(() => {
        getMessageNotReadCount().then(countInfo =>
          globalStore.notificationSubject.next(countInfo),
        );
      });
    globalStore.readNotification
      .pipe(takeUntil(appDistory))
      .subscribe(({messageId, messageType}) => {
        postReadMessage(messageId, messageType).then(() => {
          globalStore.refreshNotification.next();
        });
      });
    globalStore.updateAmount
      .pipe(throttleTime(200), takeUntil(appDistory))
      .subscribe(({gameType}) => {
        globalStore.token &&
          getBalance(gameType).then(res => {
            const {totalBalance} = res;
            const amount = totalBalance;
            globalStore.setAmount(amount);
          });
      });
    globalStore.openSports
      .pipe(takeUntil(appDistory))
      .subscribe((path?: string) => {
        // goTo(homePage, {screen: 'Sports'});
        console.log('openSports path', path);
        goTo('Sports', {path});
      });
  };

  const checkUpdate = async () => {
    if (__DEV__ || !envConfig.codePushKey || globalStore.isWeb) {
      return;
    }
    try {
      remoteBundleRef.current = await CodePush.checkForUpdate(
        envConfig.codePushKey,
      );
      setTimeout(() => {
        startBackDownload(remoteBundleRef.current);
      }, 5000);
      // await checkRemoteBoundle(remoteBundleRef.current);
    } catch (error) {
      const {log} = console;
      log(error);
    }
  };

  const [chckedLang, setCheckLang] = React.useState(false);
  const checkLang = async () => {
    const cachedLanguage = await globalStore.asyncGetItem('language');
    const language = resolveLanguage(cachedLanguage);

    globalStore.lang = language;
    await i18n.changeLanguage(language);
    setCheckLang(true);
  };

  const initApp = () => {
    setToken();
    setUserInfo();
    checkLang();
    globalSubscriptions();
    globalStore.asyncGetItem('last_record_time').then(res => {
      const timeCode = parseInt(res || '0', 10);
      if (
        !timeCode ||
        timeCode < new Date(new Date().toLocaleDateString()).getTime()
      ) {
        setTimeout(() => {
          dailyRecord();
          globalStore.asyncSetItem(
            'last_record_time',
            new Date().getTime() + '',
          );
        }, 600);
      }
    });
    if (globalStore.isWeb) {
      if (inApp) {
        checkUpdateNew();
      }
    }
    if (globalStore.isAndroid) {
      checkUpdate();
      initChat();
      setTimeout(() => {
        versionModal.handleUpdate();
      }, 1000);
    }
  };

  React.useEffect(() => {
    globalStore.asyncGetItem('channel').then(c => {
      globalStore.channel = c || defaultChannel;
      globalStore.asyncSetItem('hasChecked', globalStore.channel);
      if (!shouldShowH5MigrationGuide) {
        initApp();
      }
    });
    return () => {
      appDistory.next(true);
      appDistory.complete();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowH5MigrationGuide]);

  const startBackDownload = async (remoteBund: SafeAny) => {
    if (remoteBund) {
      downloadToastShow({
        type: 'weak',
        message: i18n.t('splash.tip.hasNew'),
      });
      downloadLock.current = true;
      const bundle = await remoteBund.download();
      await bundle.install(CodePush.InstallMode.ON_NEXT_RESTART);
      await CodePush.notifyAppReady();
      if (remoteBund.isMandatory) {
        confirmShow(
          i18n.t('splash.tip.alertTitle'),
          i18n.t('splash.tip.alertContent'),
          async () => {
            setTimeout(() => {
              CodePush.restartApp();
            }, 100);
          },
          () => {},
          true,
        );
      } else {
        downloadToastShow({
          type: 'weak',
          message: i18n.t('splash.tip.alertContent'),
        });
      }
    }
  };

  const versionModal = useVersionModal(
    true,
    versionInfo => {
      if (versionInfo) {
        // 如果需要更新,就不触发弹窗
        return;
      }
    },
    false,
  );
  const checkLangRef = React.useRef(false);
  React.useEffect(() => {
    checkLangRef.current = chckedLang;
    if (chckedLang && !shouldShowH5MigrationGuide) {
      checkUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chckedLang, shouldShowH5MigrationGuide]);

  const betbyRender = useMemo(() => {
    return (
      <div
        className="absolute left-0 top-0 hidden h-screen w-full flex-col overflow-hidden"
        id="betby-container">
        <DetailNavTitle
          onBack={() => globalStore.betbyBackSubject.next('back')}
          hideServer
          title="Sports"
        />
        <div className="flex-1 overflow-auto" id="betby">
          <Spin loading style={theme.fill.fill} />
        </div>
      </div>
    );
  }, []);
  const {run: notifyRouteChange} = useDebounceFn(
    () => {
      globalStore.routeChangeSubject.next(navigationRef?.getCurrentRoute()!);
    },
    {
      wait: 300,
    },
  );
  const stateChange = React.useCallback(() => {
    const currentRoute = navigationRef.getCurrentRoute()?.name;
    setHomeSequentialReady(isHomeSequentialRoute(currentRoute));
    notifyRouteChange();
  }, [notifyRouteChange]);
  const handleNavigationReady = React.useCallback(() => {
    setHomeSequentialReady(
      isHomeSequentialRoute(navigationRef.getCurrentRoute()?.name),
    );
  }, []);

  return (
    <SafeAreaProvider style={[theme.position.rel]}>
      <ModalManagerProvider>
        <RechargeGiftModalController />
        <React.Fragment>
          {Platform.OS === 'web' ? (
            <style type="text/css">{`
        @font-face {
          font-family: 'MaterialCommunityIcons';
          src: url(${require('react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf')}) format('truetype');
        }
        ::-webkit-scrollbar{
          display: none;
        }
      `}</style>
          ) : null}
        </React.Fragment>
        <StatusBar barStyle="light-content" />
        <SafeAreaView
          style={[
            globalStore.isWeb &&
              ({
                height: '100%',
                width: '100vw',
                maxWidth: '500px',
                marginHorizontal: 'auto',
              } as BasicObject),
            // theme.flex.col,
            // theme.overflow.hidden,
            theme.position.rel,
          ]}>
          {shouldShowH5MigrationGuide ? (
            <View style={[theme.fill.fill]}>
              <Spin loading style={theme.fill.fill} />
            </View>
          ) : (
            <VipUpgradeProvider>
              <View style={[theme.fill.fill]}>
                <NavigationContainer
                  onReady={handleNavigationReady}
                  onStateChange={stateChange}
                  ref={navigationRef}
                  linking={linking}>
                  <Stack.Navigator
                    screenOptions={{
                      headerShown: false,
                      cardStyleInterpolator: globalStore.isWeb
                        ? undefined
                        : CardStyleInterpolators.forHorizontalIOS,
                    }}>
                    <Stack.Screen name="Index" component={MainNav} />
                    {Object.values(routes).map(route => (
                      <Stack.Screen
                        key={route.name}
                        name={route.name}
                        component={route.component}
                        navigationKey={route.name}
                        options={{headerShown: route.headerShown || false}}
                      />
                    ))}
                  </Stack.Navigator>
                </NavigationContainer>
                <SportsIframeHost />
                {betbyRender}
              </View>
            </VipUpgradeProvider>
          )}
        </SafeAreaView>
        {!shouldShowH5MigrationGuide && (
          <>
            {homeSequentialReady ? <HomeSequential /> : null}
            <DialogLoading isVisible={globalLoading} />
            {renderToast}
            {renderDownloadToast}
            {versionModal.versionModal.renderModal}
            {/*{list.length > 0 && (*/}
            {/*  <PromotionModal*/}
            {/*    popVisible={popVisible}*/}
            {/*    setPopVisible={setPopVisible}*/}
            {/*    list={list}*/}
            {/*  />*/}
            {/*)}*/}
            {renderConfirmModal}
            <SharePanelProvider />

            <ParabolaProvider />
            <PushBox />
          </>
        )}
      </ModalManagerProvider>
    </SafeAreaProvider>
  );
}

if (globalStore.isAndroid) {
  let codePushOptions = {checkFrequency: CodePush.CheckFrequency.MANUAL};
  AppWithCodePush = CodePush(codePushOptions)(App);
}
if (globalStore.isWeb) {
  setupAdjust();
  setAdidAndTrackerName();
}

export default globalStore.isWeb ? App : AppWithCodePush;
